import React, { useState } from "react";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import InlineTimelineVisualization from "./InlineTimelineVisualization";
import "../../styles/tracking-system/inline-timeline.css";
import "../../styles/tracking-system/attendance-logs.css";
import moment from "moment";
import BreakLogs from "./BreakLogs";
import Modal from "react-modal";
import LocationTrackingLogs from "./LocationTrackingLogs";
import ReactVortoxSpinner from "../common-components/ReactVortoxSpinner";

const AttendenceLogs = ({
  attendanceLogsData,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  userInfoData,
  attendanceLogsLoading,
}) => {
  const [isBreakLogsModalOpen, setIsBreakLogsModalOpen] = useState(false);
  const [isLocationLogsModalOpen, setIsLocationLogsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleCloseModal = () => {
    setIsBreakLogsModalOpen(false);
    setIsLocationLogsModalOpen(false);
  };

  const attendanceLogsColumns = [
    {
      dataField: "date",
      text: "Date",
      formatter: (cell, row) => {
        // Expecting row.date in "DD-MM-YYYY" or similar
        let formattedDate = "-";
        if (row?.date) {
          // Try to parse with moment, fallback to raw if invalid
          const m = moment(
            row.date,
            ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
            true
          );
          formattedDate = m.isValid() ? m.format("ddd, D MMM") : row.date;
        }
        return (
          <div className="">
            <div className="">
              <p className="tb-data">{formattedDate}</p>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "name",
      text: "Employee",
      formatter: (cell, row) => {
        return (
          <div className="">
            <div className="employee-info">
              <div className="employee-header">
                <p className="tb-data font-weight-bold employee-name">
                  <span role="img" aria-label="user" style={{ marginRight: 6 }}>
                    👤
                  </span>
                  {row?.name || "-"}
                </p>
              </div>

              <small className="tb-data text-muted">
                <span role="img" aria-label="mobile" style={{ marginRight: 4 }}>
                  📱
                </span>
                +91 {row?.mobile || ""}
              </small>
              <span
                className={`account-type-badge ${
                  row?.account_type?.toLowerCase() || "default"
                }`}
              >
                {row?.account_type || "N/A"}
              </span>
              <span className="gym-badge">{row?.gym_name || ""}</span>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "attendance",
      text: "Attendance Status",
      formatter: (cell, row) => {
        const attendanceArray = row?.attendance || [];

        // Helper function to format time difference
        const formatTimeDiff = (timeStr) => {
          if (!timeStr || timeStr === "00:00") return null;
          const [h, m] = timeStr.split(":").map(Number);
          if (h > 0 && m > 0) return `${h}h ${m}m`;
          if (h > 0) return `${h}h`;
          return `${m}m`;
        };

        // Function to get shift status
        const getShiftStatus = (shift) => {
          const checkInTime = shift?.check_in_time;
          const checkOutTime = shift?.check_out_time;
          const lateArrival = shift?.late_arrival;
          const earlyArrival = shift?.early_arrival;

          // Determine arrival status
          let arrivalStatus = "on-time";
          let arrivalText = "On time";
          let arrivalColor = "#28a745";

          if (earlyArrival && earlyArrival !== "00:00") {
            const timeDiff = formatTimeDiff(earlyArrival);
            arrivalStatus = "early";
            arrivalText = timeDiff ? `${timeDiff} early` : "Early";
            arrivalColor = "#28a745";
          } else if (lateArrival && lateArrival !== "00:00") {
            const timeDiff = formatTimeDiff(lateArrival);
            arrivalStatus = "late";
            arrivalText = timeDiff ? `${timeDiff} late` : "Late";
            arrivalColor = "#dc3545";
          }

          return {
            checkInTime,
            checkOutTime,
            arrivalStatus,
            arrivalText,
            arrivalColor,
            isCheckedIn: !!checkInTime,
            isCheckedOut: !!checkOutTime,
          };
        };

        // If no attendance data
        if (!attendanceArray || attendanceArray.length === 0) {
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 10px",
                borderRadius: "4px",
                backgroundColor: "#f8d7da",
                fontSize: "11px",
              }}
            >
              <span
                style={{
                  color: "#dc3545",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                &#x2716;
              </span>
              <span style={{ color: "#dc3545", fontWeight: "500" }}>
                No attendance data
              </span>
            </div>
          );
        }

        // Single shift - display full details
        if (attendanceArray.length === 1) {
          const shift = attendanceArray[0];
          const status = getShiftStatus(shift);

          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              {/* Shift Header */}
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {shift.role || "Shift"}
              </div>

              {/* Check In/Out Status */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  backgroundColor: status.isCheckedIn ? "#d4edda" : "#f8d7da",
                  fontSize: "11px",
                }}
              >
                <span
                  style={{
                    color: status.isCheckedIn ? "#28a745" : "#dc3545",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  {status.isCheckedIn ? "✓" : "✗"}
                </span>
                <span
                  style={{
                    fontWeight: "500",
                  }}
                >
                  {status.isCheckedIn ? (
                    <>
                      <span style={{ color: "#28a745" }}>
                        In: {status.checkInTime}
                      </span>
                      <span style={{ color: status.arrivalColor }}>
                        ({status.arrivalText})
                      </span>
                    </>
                  ) : (
                    <span style={{ color: "#dc3545" }}>Not checked in</span>
                  )}
                </span>

                {status.isCheckedIn && (
                  <>
                    <span style={{ color: "#dee2e6", margin: "0 4px" }}>|</span>
                    <span
                      style={{
                        color: status.isCheckedOut ? "#28a745" : "#dc3545",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {status.isCheckedOut ? "✓" : "✗"}
                    </span>
                    <span
                      style={{
                        color: status.isCheckedOut ? "#28a745" : "#dc3545",
                        fontWeight: "500",
                      }}
                    >
                      {status.isCheckedOut
                        ? `Out: ${status.checkOutTime}`
                        : "Not out"}
                    </span>
                  </>
                )}
              </div>
            </div>
          );
        }

        // Multiple shifts - compact view
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div
              style={{
                fontSize: "10px",
                color: "#6b7280",
                fontWeight: "600",
                textAlign: "center",
                marginBottom: "2px",
              }}
            >
              {attendanceArray.length} Shifts
            </div>

            {attendanceArray.map((shift, index) => {
              const status = getShiftStatus(shift);

              return (
                <div
                  key={shift.shift_id || index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "3px 6px",
                    borderRadius: "3px",
                    backgroundColor: status.isCheckedIn ? "#d4edda" : "#f8d7da",
                    fontSize: "10px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#6b7280",
                      fontWeight: "600",
                      minWidth: "12px",
                    }}
                  >
                    {index + 1}.
                  </span>

                  <span
                    style={{
                      color: status.isCheckedIn ? "#28a745" : "#dc3545",
                      fontSize: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {status.isCheckedIn ? "✓" : "✗"}
                  </span>

                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "10px",
                    }}
                  >
                    {status.isCheckedIn ? (
                      <>
                        <span style={{ color: "#28a745" }}>
                          {status.checkInTime}
                        </span>
                        <span style={{ color: status.arrivalColor }}>
                          ({status.arrivalText})
                        </span>
                      </>
                    ) : (
                      <span style={{ color: "#dc3545" }}>No check-in</span>
                    )}
                  </span>

                  {status.isCheckedIn && (
                    <span
                      style={{
                        color: status.isCheckedOut ? "#28a745" : "#dc3545",
                        fontSize: "9px",
                        fontWeight: "500",
                        marginLeft: "auto",
                      }}
                    >
                      {status.isCheckedOut
                        ? `✓ ${status.checkOutTime}`
                        : "✗ No out"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      dataField: "shifts",
      text: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span className="">Tracking Visual</span>
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "4px",
              fontSize: "10px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "12px",
                  height: "8px",
                  background: "#4a90e2",
                  borderRadius: "2px",
                }}
              ></div>
              <span style={{ color: "#6b7280", fontWeight: "500" }}>Shift</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "12px",
                  height: "8px",
                  background: "#10b981",
                  borderRadius: "2px",
                }}
              ></div>
              <span style={{ color: "#6b7280", fontWeight: "500" }}>Break</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "12px",
                  height: "8px",
                  background: "#dc3545",
                  borderRadius: "2px",
                }}
              ></div>
              <span style={{ color: "#6b7280", fontWeight: "500" }}>
                Out of Range
              </span>
            </div>
          </div>
        </div>
      ),
      formatter: (cell, row) => {
        return (
          <div className="tracking-visual-cell">
            <InlineTimelineVisualization
              shifts={row?.shifts || []}
              breaks={row?.breaks || []}
              summary={row?.summary || {}}
            />
          </div>
        );
      },
    },

    {
      dataField: "summary",
      text: "Summary",
      formatter: (cell, row) => {
        const summary = row?.summary || {};
        return (
          <div className="summary-cell">
            <div className="summary-item">
              <span className="summary-label">Work Hours:</span>
              <span className="summary-value">
                {summary.working_hours_hms
                  ? moment
                      .duration(summary.working_hours_hms)
                      .asHours()
                      .toFixed(1) + "h"
                  : "-"}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Breaks:</span>
              <span className="summary-value">{summary.total_breaks || 0}</span>
            </div>
            {summary.out_of_range_count > 0 && (
              <div className="summary-item warning">
                <span className="summary-label">Out of Range:</span>
                <span className="summary-value text-danger">
                  {summary.out_of_range_count}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      dataField: "date",
      text: "Logs",
      formatter: (cell, row) => {
        return (
          <div
            className="attendance-logs-actions-wrapper"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              justifyContent: "center",
              fontSize: "18px", // set text size to 15px
            }}
          >
            <button
              className="logs-action-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "#f3f6fa",
                border: "1px solid #d1e3f8",
                color: "#2563eb",
                borderRadius: "5px",
                padding: "4px 10px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                transition: "background 0.2s, border 0.2s",
                boxShadow: "0 1px 2px rgba(37,99,235,0.04)",
                minWidth: "0",
              }}
              onClick={() => {
                setSelectedUser(row);
                setIsBreakLogsModalOpen(true);
              }}
              title="View Break Logs"
            >
              <span
                style={{
                  display: "inline-block",
                  width: "15px",
                  height: "15px",
                  background: "transparent",
                  marginRight: "1px",
                  fontSize: "14px",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                {/* Simple eye icon SVG, not using react-icons */}
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                  <ellipse
                    cx="10"
                    cy="10"
                    rx="7"
                    ry="5"
                    stroke="#2563eb"
                    strokeWidth="1.2"
                    fill="#eaf1fb"
                  />
                  <circle cx="10" cy="10" r="2" fill="#2563eb" />
                </svg>
              </span>
              Break
            </button>
            <button
              className="logs-action-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "#f3f6fa",
                border: "1px solid #d1e3f8",
                color: "#0e7490",
                borderRadius: "5px",
                padding: "4px 10px",
                fontWeight: 500,
                fontSize: "14px",
                cursor: "pointer",
                transition: "background 0.2s, border 0.2s",
                boxShadow: "0 1px 2px rgba(14,116,144,0.04)",
                minWidth: "0",
              }}
              onClick={() => {
                setSelectedUser(row);
                setIsLocationLogsModalOpen(true);
              }}
              title="View Location Logs"
            >
              <span
                style={{
                  display: "inline-block",
                  width: "15px",
                  height: "15px",
                  background: "transparent",
                  marginRight: "1px",
                  fontSize: "14px",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                {/* Simple location pin icon SVG, not using react-icons */}
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 18c3.866-5.333 6-8.333 6-11A6 6 0 1 0 4 7c0 2.667 2.134 5.667 6 11z"
                    stroke="#0e7490"
                    strokeWidth="1.2"
                    fill="#e0f2fe"
                  />
                  <circle cx="10" cy="7" r="1.7" fill="#0e7490" />
                </svg>
              </span>
              Location
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table break-logs-table-container">
        {" "}
        {attendanceLogsLoading ? (
          <ReactVortoxSpinner />
        ) : (
          <PaymentLinkTable
            tableColumns={attendanceLogsColumns}
            tableData={attendanceLogsData?.data || []}
            tablePagination={attendanceLogsData?.pagination}
            isPage={isPage}
            setIsPage={(value) => setIsPage(value)}
            setPageLimit={setPageLimit}
            pageLimit={pageLimit}
            isFirstColSticky={true}
          />
        )}
      </div>

      <Modal
        isOpen={isBreakLogsModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Break Logs Modal"
        ariaHideApp={false}
      >
        <BreakLogs
          userId={selectedUser?.user_id}
          userInfoData={userInfoData}
          onCloseModal={handleCloseModal}
          date={
            selectedUser?.date
              ? moment(selectedUser.date, "DD-MM-YYYY").format("YYYY-MM-DD")
              : null
          }
        />
      </Modal>

      <Modal
        isOpen={isLocationLogsModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Location Logs Modal"
        ariaHideApp={false}
      >
        <LocationTrackingLogs
          userId={selectedUser?.user_id}
          userInfoData={userInfoData}
          onCloseModal={handleCloseModal}
          date={
            selectedUser?.date
              ? moment(selectedUser.date, "DD-MM-YYYY").format("YYYY-MM-DD")
              : null
          }
        />
      </Modal>
    </>
  );
};

export default AttendenceLogs;

// {
//     dataField: "date",
//     text: <p className="table__header">Logs</p>,
//     formatter: (cell, row) => {
//       return (
//         <div
//           className=""
//           style={{
//             display: "flex",
//             alignItems: "center",
//             gap: "10px",
//             justifyContent: "center",
//           }}
//         >
//           <div
//             className="underline"
//             onClick={(e) => {
//               e.stopPropagation();
//               //   handleViewBreakDetails(row);
//             }}
//           >
//             <span className="timeline-icon">{/* <EyeIcon /> */}</span>
//             <span>Break</span>
//           </div>
//           <div
//             className="action-btn break-details-btn"
//             onClick={(e) => {
//               e.stopPropagation();
//               if (handleViewBreakDetails) {
//                 handleViewBreakDetails(row);
//               }
//             }}
//             title="View detailed workday breakdown"
//           >
//             <span className="timeline-icon">{/* <EyeIcon /> */}</span>
//             <span>Location</span>
//           </div>
//         </div>
//       );
//     },
//   },
